/** Placement ids passed to AdContainer. Each maps to one ads_* config flag. */
export const AD_PLACEMENTS = [
  'public_analyzer_wait',
  'job_fit_wait',
  'salary_wait',
  'headline_wait',
  'cover_letter_wait',
  'bio_wait',
  'home_upload_wait',
  'analyse_dialog_wait',
  'import_wait',
  'career_insights_wait',
  'dashboard',
  'resume_list',
  'job_applications',
  'resume_access',
  'interview_prep_list',
  'landing_home',
  'landing_resume_analyzer',
  'landing_europass_cv_builder',
  'landing_uae_cv_builder',
  'landing_examples_europass',
  'landing_examples_uae_jobs',
  'landing_examples_freshers',
] as const;

export type AdPlacementId = (typeof AD_PLACEMENTS)[number];

export type AdSettingsFlagKey =
  | 'ads_enabled'
  | 'ads_mobile_enabled'
  | `ads_${AdPlacementId}`;

export type AdSettingsFlags = Record<AdSettingsFlagKey, 'yes' | 'no'>;

export type PlacementDefinition = {
  flagKey: AdSettingsFlagKey;
  /** When false, phones never show this placement. */
  mobileAllowed: boolean;
  slotId: string;
};

/** Public waiting rooms share the analyzer unit; salary has its own; lists use sidebar. */
export const PUBLIC_WAIT_SLOT = '8722339999';
export const SALARY_LOADER_SLOT = '7318225574';
export const SIDEBAR_SLOT = '8515465595';
/**
 * Responsive Display unit for Phase 1 landing in-content ads.
 * Ops: create an AdSense unit named "Landing in-content" and replace this id
 * for separate reporting; until then the sidebar responsive unit is reused.
 */
export const LANDING_INCONTENT_SLOT = SIDEBAR_SLOT;

export const PLACEMENT_DEFINITIONS: Record<AdPlacementId, PlacementDefinition> = {
  public_analyzer_wait: {
    flagKey: 'ads_public_analyzer_wait',
    mobileAllowed: true,
    slotId: PUBLIC_WAIT_SLOT,
  },
  job_fit_wait: {
    flagKey: 'ads_job_fit_wait',
    mobileAllowed: true,
    slotId: PUBLIC_WAIT_SLOT,
  },
  salary_wait: {
    flagKey: 'ads_salary_wait',
    mobileAllowed: true,
    slotId: SALARY_LOADER_SLOT,
  },
  headline_wait: {
    flagKey: 'ads_headline_wait',
    mobileAllowed: true,
    slotId: PUBLIC_WAIT_SLOT,
  },
  cover_letter_wait: {
    flagKey: 'ads_cover_letter_wait',
    mobileAllowed: true,
    slotId: PUBLIC_WAIT_SLOT,
  },
  bio_wait: {
    flagKey: 'ads_bio_wait',
    mobileAllowed: true,
    slotId: PUBLIC_WAIT_SLOT,
  },
  home_upload_wait: {
    flagKey: 'ads_home_upload_wait',
    mobileAllowed: true,
    slotId: PUBLIC_WAIT_SLOT,
  },
  analyse_dialog_wait: {
    flagKey: 'ads_analyse_dialog_wait',
    mobileAllowed: true,
    slotId: SIDEBAR_SLOT,
  },
  import_wait: {
    flagKey: 'ads_import_wait',
    mobileAllowed: true,
    slotId: SIDEBAR_SLOT,
  },
  career_insights_wait: {
    flagKey: 'ads_career_insights_wait',
    mobileAllowed: true,
    slotId: SIDEBAR_SLOT,
  },
  dashboard: {
    flagKey: 'ads_dashboard',
    mobileAllowed: false,
    slotId: SIDEBAR_SLOT,
  },
  resume_list: {
    flagKey: 'ads_resume_list',
    mobileAllowed: false,
    slotId: SIDEBAR_SLOT,
  },
  job_applications: {
    flagKey: 'ads_job_applications',
    mobileAllowed: false,
    slotId: SIDEBAR_SLOT,
  },
  resume_access: {
    flagKey: 'ads_resume_access',
    mobileAllowed: false,
    slotId: SIDEBAR_SLOT,
  },
  interview_prep_list: {
    flagKey: 'ads_interview_prep_list',
    mobileAllowed: false,
    slotId: SIDEBAR_SLOT,
  },
  landing_home: {
    flagKey: 'ads_landing_home',
    mobileAllowed: true,
    slotId: LANDING_INCONTENT_SLOT,
  },
  landing_resume_analyzer: {
    flagKey: 'ads_landing_resume_analyzer',
    mobileAllowed: true,
    slotId: LANDING_INCONTENT_SLOT,
  },
  landing_europass_cv_builder: {
    flagKey: 'ads_landing_europass_cv_builder',
    mobileAllowed: true,
    slotId: LANDING_INCONTENT_SLOT,
  },
  landing_uae_cv_builder: {
    flagKey: 'ads_landing_uae_cv_builder',
    mobileAllowed: true,
    slotId: LANDING_INCONTENT_SLOT,
  },
  landing_examples_europass: {
    flagKey: 'ads_landing_examples_europass',
    mobileAllowed: true,
    slotId: LANDING_INCONTENT_SLOT,
  },
  landing_examples_uae_jobs: {
    flagKey: 'ads_landing_examples_uae_jobs',
    mobileAllowed: true,
    slotId: LANDING_INCONTENT_SLOT,
  },
  landing_examples_freshers: {
    flagKey: 'ads_landing_examples_freshers',
    mobileAllowed: true,
    slotId: LANDING_INCONTENT_SLOT,
  },
};

export const ADSENSE_CLIENT_ID = 'ca-pub-6494790779913308';

export const MOBILE_BREAKPOINT_PX = 768;

export function emptyAdSettingsFlags(): AdSettingsFlags {
  return {
    ads_enabled: 'no',
    ads_mobile_enabled: 'no',
    ads_public_analyzer_wait: 'no',
    ads_job_fit_wait: 'no',
    ads_salary_wait: 'no',
    ads_headline_wait: 'no',
    ads_cover_letter_wait: 'no',
    ads_bio_wait: 'no',
    ads_home_upload_wait: 'no',
    ads_analyse_dialog_wait: 'no',
    ads_import_wait: 'no',
    ads_career_insights_wait: 'no',
    ads_dashboard: 'no',
    ads_resume_list: 'no',
    ads_job_applications: 'no',
    ads_resume_access: 'no',
    ads_interview_prep_list: 'no',
    ads_landing_home: 'no',
    ads_landing_resume_analyzer: 'no',
    ads_landing_europass_cv_builder: 'no',
    ads_landing_uae_cv_builder: 'no',
    ads_landing_examples_europass: 'no',
    ads_landing_examples_uae_jobs: 'no',
    ads_landing_examples_freshers: 'no',
  };
}

export function normalizeAdSettingsFlags(
  raw: Partial<Record<string, string>> | null | undefined,
): AdSettingsFlags {
  const flags = emptyAdSettingsFlags();
  if (!raw || typeof raw !== 'object') {
    return flags;
  }
  for (const key of Object.keys(flags) as AdSettingsFlagKey[]) {
    flags[key] = raw[key] === 'yes' ? 'yes' : 'no';
  }
  return flags;
}
